import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Building, Plus, Trash, Pencil, Users, PaperPlaneTilt, UserCircle, EnvelopeSimple, Check, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Disciplines list
const DISCIPLINES = [
  'Civil', 'Structural', 'Architectural', 'Mechanical', 'Electrical', 
  'Instrumentation', 'Piping', 'Process', 'HVAC', 'Fire Protection',
  'Telecom', 'Environmental', 'Geotechnical', 'Survey', 'Other'
];

const DDMSettings = ({ user }) => {
  const [activeTab, setActiveTab] = useState('companies');
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [incomingRules, setIncomingRules] = useState([]);
  const [outgoingRules, setOutgoingRules] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [incomingDialogOpen, setIncomingDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  
  // Form states
  const [companyForm, setCompanyForm] = useState({ name: '', code: '', description: '', project_codes: '' });
  const [contactForm, setContactForm] = useState({ name: '', email: '', role: '', email_type: 'to' });
  const [incomingForm, setIncomingForm] = useState({
    scope_type: 'company',
    scope_value: '',
    discipline: '',
    responsible_engineer_id: '',
    discipline_reviewer_id: '',
    for_information_only_ids: [],
    project_manager_id: '',
    document_controller_id: ''
  });

  useEffect(() => {
    fetchCompanies();
    fetchUsers();
    fetchIncomingRules();
    fetchOutgoingRules();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchIncomingRules = async () => {
    try {
      const response = await axios.get(`${API}/ddm/incoming`);
      setIncomingRules(response.data);
    } catch (error) {
      console.error('Failed to fetch incoming DDM rules');
    }
  };

  const fetchOutgoingRules = async () => {
    try {
      const response = await axios.get(`${API}/ddm/outgoing`);
      setOutgoingRules(response.data);
    } catch (error) {
      console.error('Failed to fetch outgoing DDM rules');
    }
  };

  // Company handlers
  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...companyForm,
        project_codes: companyForm.project_codes.split(',').map(s => s.trim()).filter(Boolean)
      };
      await axios.post(`${API}/companies`, data);
      toast.success('Company created successfully');
      setCompanyDialogOpen(false);
      setCompanyForm({ name: '', code: '', description: '', project_codes: '' });
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create company');
    }
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...companyForm,
        project_codes: companyForm.project_codes.split(',').map(s => s.trim()).filter(Boolean)
      };
      await axios.put(`${API}/companies/${editingCompany.id}`, data);
      toast.success('Company updated');
      setCompanyDialogOpen(false);
      setEditingCompany(null);
      setCompanyForm({ name: '', code: '', description: '', project_codes: '' });
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to update company');
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;
    try {
      await axios.delete(`${API}/companies/${companyId}`);
      toast.success('Company deleted');
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to delete company');
    }
  };

  const openEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      name: company.name,
      code: company.code,
      description: company.description || '',
      project_codes: (company.project_codes || []).join(', ')
    });
    setCompanyDialogOpen(true);
  };

  // Contact handlers
  const handleAddContact = async (e) => {
    e.preventDefault();
    if (!selectedCompany) return;
    try {
      await axios.post(`${API}/companies/${selectedCompany.id}/contacts`, contactForm);
      toast.success('Contact added');
      setContactDialogOpen(false);
      setContactForm({ name: '', email: '', role: '', email_type: 'to' });
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to add contact');
    }
  };

  const handleDeleteContact = async (companyId, contactId) => {
    if (!window.confirm('Remove this contact?')) return;
    try {
      await axios.delete(`${API}/companies/${companyId}/contacts/${contactId}`);
      toast.success('Contact removed');
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to remove contact');
    }
  };

  // Incoming DDM handlers
  const handleCreateIncomingRule = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/ddm/incoming`, incomingForm);
      toast.success('DDM rule created');
      setIncomingDialogOpen(false);
      setIncomingForm({
        scope_type: 'company',
        scope_value: '',
        discipline: '',
        responsible_engineer_id: '',
        discipline_reviewer_id: '',
        for_information_only_ids: [],
        project_manager_id: '',
        document_controller_id: ''
      });
      fetchIncomingRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create DDM rule');
    }
  };

  const handleDeleteIncomingRule = async (ruleId) => {
    if (!window.confirm('Delete this DDM rule?')) return;
    try {
      await axios.delete(`${API}/ddm/incoming/${ruleId}`);
      toast.success('DDM rule deleted');
      fetchIncomingRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const getUserName = (userId) => {
    const u = users.find(user => user.id === userId);
    return u ? u.full_name : '-';
  };

  const getCompanyName = (companyId) => {
    const c = companies.find(company => company.id === companyId);
    return c ? c.name : '-';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="ddm-settings-page">
      <div className="mb-6">
        <h1 className="text-3xl font-semibold text-gray-900 mb-1">
          Document Distribution Matrix
        </h1>
        <p className="text-gray-500">
          Manage company profiles and document distribution rules
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 bg-gray-100 p-1 rounded-xl">
          <TabsTrigger value="companies" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm">
            <Building size={14} className="mr-1.5" />
            Companies
          </TabsTrigger>
          <TabsTrigger value="incoming" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm">
            <Users size={14} className="mr-1.5" />
            Incoming DDM
          </TabsTrigger>
          <TabsTrigger value="outgoing" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm">
            <PaperPlaneTilt size={14} className="mr-1.5" />
            Outgoing DDM
          </TabsTrigger>
        </TabsList>

        {/* Companies Tab */}
        <TabsContent value="companies">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-base font-semibold text-gray-900">Company Profiles</h2>
            <Button size="sm" className="h-9 bg-blue-900 hover:bg-blue-800 rounded-xl" onClick={() => {
              setEditingCompany(null);
              setCompanyForm({ name: '', code: '', description: '', project_codes: '' });
              setCompanyDialogOpen(true);
            }}>
              <Plus size={14} className="mr-1.5" />
              Add Company
            </Button>
          </div>

          {companies.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-apple-sm">
              <Building size={40} className="mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">No companies yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {companies.map((company) => (
                <div key={company.id} className="bg-white rounded-2xl p-5 shadow-apple-sm border border-gray-100">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{company.name}</span>
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-lg">{company.code}</span>
                      </div>
                      {company.description && (
                        <p className="text-sm text-gray-500 mt-1">{company.description}</p>
                      )}
                      {company.project_codes?.length > 0 && (
                        <p className="text-xs text-blue-600 mt-1">
                          Projects: {company.project_codes.join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-lg hover:bg-gray-100" onClick={() => openEditCompany(company)}>
                        <Pencil size={14} className="text-gray-500" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-lg hover:bg-red-50" onClick={() => handleDeleteCompany(company.id)}>
                        <Trash size={14} className="text-red-500" />
                      </Button>
                    </div>
                  </div>

                  {/* Contacts */}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Contacts</span>
                      <Button size="sm" variant="ghost" className="h-6 text-xs px-2 rounded-lg hover:bg-gray-100" onClick={() => {
                        setSelectedCompany(company);
                        setContactDialogOpen(true);
                      }}>
                        <Plus size={12} className="mr-1" />
                        Add
                      </Button>
                    </div>
                    {company.contacts?.length > 0 ? (
                      <div className="space-y-1.5">
                        {company.contacts.map((contact) => (
                          <div key={contact.id} className="flex items-center justify-between text-sm bg-gray-50 px-3 py-2 rounded-xl">
                            <div className="flex items-center gap-2">
                              <UserCircle size={16} className="text-gray-400" />
                              <span className="font-medium text-gray-900">{contact.name}</span>
                              <span className="text-gray-500">{contact.email}</span>
                              {contact.role && (
                                <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded-md">{contact.role}</span>
                              )}
                              <span className={`text-xs px-1.5 py-0.5 rounded-md ${contact.email_type === 'to' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>
                                {contact.email_type.toUpperCase()}
                              </span>
                            </div>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 rounded-lg hover:bg-red-50" onClick={() => handleDeleteContact(company.id, contact.id)}>
                              <X size={12} className="text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400">No contacts added</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Incoming DDM Tab */}
        <TabsContent value="incoming">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Incoming DDM Rules</h2>
              <p className="text-sm text-gray-500">Define reviewer assignments by discipline</p>
            </div>
            <Button size="sm" className="h-9 bg-blue-900 hover:bg-blue-800 rounded-xl" onClick={() => setIncomingDialogOpen(true)}>
              <Plus size={14} className="mr-1.5" />
              Add Rule
            </Button>
          </div>

          {incomingRules.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-apple-sm">
              <Users size={40} className="mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">No incoming DDM rules</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-apple-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="p-3 text-left font-semibold text-gray-700">Scope</th>
                    <th className="p-3 text-left font-semibold text-gray-700">Discipline</th>
                    <th className="p-3 text-left font-semibold text-gray-700">Resp. Engineer</th>
                    <th className="p-3 text-left font-semibold text-gray-700">Discipline Rev.</th>
                    <th className="p-3 text-left font-semibold text-gray-700">FYI</th>
                    <th className="p-3 text-left font-semibold text-gray-700">PM</th>
                    <th className="p-3 text-left font-semibold text-gray-700">DC</th>
                    <th className="p-3 w-12"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {incomingRules.map((rule) => (
                    <tr key={rule.id} className="hover:bg-gray-50 transition-colors">
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 rounded-lg ${rule.scope_type === 'company' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'}`}>
                          {rule.scope_type}
                        </span>
                        <br />
                        <span className="text-gray-500 text-xs">
                          {rule.scope_type === 'company' ? getCompanyName(rule.scope_value) : rule.scope_value}
                        </span>
                      </td>
                      <td className="p-3 font-medium text-gray-900">{rule.discipline}</td>
                      <td className="p-3 text-gray-600">{rule.responsible_engineer_name || '-'}</td>
                      <td className="p-3 text-gray-600">{rule.discipline_reviewer_name || '-'}</td>
                      <td className="p-3 text-gray-600">{rule.for_information_only_names?.join(', ') || '-'}</td>
                      <td className="p-3 text-gray-600">{rule.project_manager_name || '-'}</td>
                      <td className="p-3 text-gray-600">{rule.document_controller_name || '-'}</td>
                      <td className="p-3">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-lg hover:bg-red-50" onClick={() => handleDeleteIncomingRule(rule.id)}>
                          <Trash size={12} className="text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Outgoing DDM Tab */}
        <TabsContent value="outgoing">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Outgoing DDM Rules</h2>
              <p className="text-sm text-gray-500">Define email distribution when sending documents back</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Outgoing DDM uses company contacts. Set contacts as TO or CC in the Companies tab.
              When DC confirms a document, the system will suggest recipients based on the document source company.
            </p>
          </div>

          {companies.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-apple-sm">
              <EnvelopeSimple size={40} className="mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">Add companies first to configure outgoing DDM</p>
            </div>
          ) : (
            <div className="space-y-3">
              {companies.map((company) => {
                const toContacts = company.contacts?.filter(c => c.email_type === 'to' && c.is_active !== false) || [];
                const ccContacts = company.contacts?.filter(c => c.email_type === 'cc' && c.is_active !== false) || [];
                return (
                  <div key={company.id} className="bg-white rounded-2xl p-4 shadow-apple-sm border border-gray-100">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-gray-900">{company.name}</span>
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-lg">{company.code}</span>
                    </div>
                    <div className="flex gap-6 text-sm">
                      <div>
                        <span className="text-gray-400">TO: </span>
                        {toContacts.length > 0 ? (
                          <span className="text-green-600">{toContacts.map(c => c.email).join(', ')}</span>
                        ) : (
                          <span className="text-red-500">None</span>
                        )}
                      </div>
                      <div>
                        <span className="text-gray-400">CC: </span>
                        {ccContacts.length > 0 ? (
                          <span className="text-amber-600">{ccContacts.map(c => c.email).join(', ')}</span>
                        ) : (
                          <span className="text-gray-400">None</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Company Dialog */}
      <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">
              {editingCompany ? 'Edit Company' : 'Add Company'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={editingCompany ? handleUpdateCompany : handleCreateCompany} className="space-y-3">
            <div>
              <Label className="text-xs">Company Name*</Label>
              <Input
                value={companyForm.name}
                onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                placeholder="e.g., ABC Contractors"
                className="h-8 text-sm"
                required
              />
            </div>
            <div>
              <Label className="text-xs">Company Code*</Label>
              <Input
                value={companyForm.code}
                onChange={(e) => setCompanyForm({ ...companyForm, code: e.target.value.toUpperCase() })}
                placeholder="e.g., ABC"
                className="h-8 text-sm"
                required
              />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea
                value={companyForm.description}
                onChange={(e) => setCompanyForm({ ...companyForm, description: e.target.value })}
                placeholder="Optional description..."
                rows={2}
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Project Codes (comma-separated)</Label>
              <Input
                value={companyForm.project_codes}
                onChange={(e) => setCompanyForm({ ...companyForm, project_codes: e.target.value })}
                placeholder="e.g., PRJ001, PRJ002"
                className="h-8 text-sm"
              />
            </div>
            <Button type="submit" className="w-full" size="sm">
              {editingCompany ? 'Update Company' : 'Create Company'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">
              Add Contact to {selectedCompany?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddContact} className="space-y-3">
            <div>
              <Label className="text-xs">Contact Name*</Label>
              <Input
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                placeholder="e.g., John Smith"
                className="h-8 text-sm"
                required
              />
            </div>
            <div>
              <Label className="text-xs">Email*</Label>
              <Input
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                placeholder="e.g., john@company.com"
                className="h-8 text-sm"
                required
              />
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Input
                value={contactForm.role}
                onChange={(e) => setContactForm({ ...contactForm, role: e.target.value })}
                placeholder="e.g., Document Controller"
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Email Type*</Label>
              <Select value={contactForm.email_type} onValueChange={(val) => setContactForm({ ...contactForm, email_type: val })}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="to">TO (Primary recipient)</SelectItem>
                  <SelectItem value="cc">CC (Copy)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full" size="sm">
              Add Contact
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Incoming DDM Rule Dialog */}
      <Dialog open={incomingDialogOpen} onOpenChange={setIncomingDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base">Add Incoming DDM Rule</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateIncomingRule} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Scope Type*</Label>
                <Select value={incomingForm.scope_type} onValueChange={(val) => setIncomingForm({ ...incomingForm, scope_type: val, scope_value: '' })}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">Per Company</SelectItem>
                    <SelectItem value="project">Per Project</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{incomingForm.scope_type === 'company' ? 'Company*' : 'Project Code*'}</Label>
                {incomingForm.scope_type === 'company' ? (
                  <Select value={incomingForm.scope_value} onValueChange={(val) => setIncomingForm({ ...incomingForm, scope_value: val })}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={incomingForm.scope_value}
                    onChange={(e) => setIncomingForm({ ...incomingForm, scope_value: e.target.value })}
                    placeholder="e.g., PRJ001"
                    className="h-8 text-sm"
                    required
                  />
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs">Discipline*</Label>
              <Select value={incomingForm.discipline} onValueChange={(val) => setIncomingForm({ ...incomingForm, discipline: val })}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select discipline" />
                </SelectTrigger>
                <SelectContent>
                  {DISCIPLINES.map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="border-t pt-2">
              <p className="text-xs font-medium mb-2">Reviewer Assignments</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Responsible Engineer</Label>
                  <Select value={incomingForm.responsible_engineer_id} onValueChange={(val) => setIncomingForm({ ...incomingForm, responsible_engineer_id: val })}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Discipline Reviewer</Label>
                  <Select value={incomingForm.discipline_reviewer_id} onValueChange={(val) => setIncomingForm({ ...incomingForm, discipline_reviewer_id: val })}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Project Manager</Label>
                  <Select value={incomingForm.project_manager_id} onValueChange={(val) => setIncomingForm({ ...incomingForm, project_manager_id: val })}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Document Controller</Label>
                  <Select value={incomingForm.document_controller_id} onValueChange={(val) => setIncomingForm({ ...incomingForm, document_controller_id: val })}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Button type="submit" className="w-full" size="sm">
              Create DDM Rule
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DDMSettings;
